import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../services/AxiosInstance';
import { getCreditOverview } from '../../services/CreditService';
import { getActiveSubscriptions } from '../../utils/subscriptionCache';
import {
  getModuleTabIcon,
  getModuleTabLabel,
  subscriptionMatchesModuleTab,
} from '../../utils/subscriptionModuleTabs';
import { getVaultCryptoActivities } from '../../services/CredentialVaultService';
import { formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import './businessDashboard.css';

/** Fixed lookback window for overview metrics (no day selector on main dashboard). */
const DASHBOARD_RANGE_DAYS = 30;

const SUBSCRIPTION_MODULES = ['task', 'document', 'discussion_forum', 'invoice_scanner'];
const STORAGE_MODULES = [
  { key: 'task', label: 'Tasks', storageField: 'task_storage', barClass: 'is-tasks' },
  { key: 'document', label: 'Documents', storageField: 'disk_space', barClass: 'is-documents' },
  { key: 'discussion_forum', label: 'Discussion Forum', storageField: 'forum_storage', barClass: 'is-forum' },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getRangeBounds(rangeDays) {
  const to = startOfDay(new Date());
  const from = new Date(to);
  from.setDate(from.getDate() - (Number(rangeDays) > 0 ? Number(rangeDays) : 30));
  return { from, to };
}

function parseDay(dateValue) {
  if (!dateValue) return null;
  const d = startOfDay(dateValue);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Inclusive calendar-day check within [from, to]. */
function isDateInRange(dateValue, from, to) {
  const d = parseDay(dateValue);
  if (!d || !from || !to) return false;
  return d >= from && d <= to;
}

function itemTouchesRange(dateValues, from, to) {
  return (dateValues || []).some((value) => isDateInRange(value, from, to));
}

/** True when [start, end] overlaps [from, to] (missing end/start treated as a point). */
function periodOverlapsRange(startValue, endValue, from, to) {
  const start = parseDay(startValue);
  const end = parseDay(endValue);
  if (!start && !end) return false;
  const s = start || end;
  const e = end || start;
  return s <= to && e >= from;
}

function projectInSelectedRange(project, from, to) {
  if (
    itemTouchesRange(
      [project?.created_at, project?.updated_at, project?.actual_completion_date],
      from,
      to
    )
  ) {
    return true;
  }
  return periodOverlapsRange(project?.start_date, project?.end_date, from, to);
}

function daysUntil(dateValue) {
  if (!dateValue) return null;
  const target = startOfDay(dateValue);
  if (Number.isNaN(target.getTime())) return null;
  const today = startOfDay(new Date());
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatRelativeTime(dateValue) {
  if (!dateValue) return '';
  const then = new Date(dateValue).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'Just now';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function pickSubscription(subscriptions, moduleKey) {
  return (subscriptions || []).find((sub) => subscriptionMatchesModuleTab(sub, moduleKey)) || null;
}

function getPlanName(subscription) {
  return (
    subscription?.SubscriptionPlan?.display_name ||
    subscription?.SubscriptionPlan?.name ||
    '—'
  );
}

function getSubscriptionCost(subscription) {
  const plan = subscription?.SubscriptionPlan;
  if (subscription?.total_price != null) return subscription.total_price;
  if (subscription?.amount_paid != null) return subscription.amount_paid;
  if (plan?.yearly_price != null && String(subscription?.billing_cycle || '').includes('year')) {
    return plan.yearly_price;
  }
  if (plan?.monthly_price != null) return plan.monthly_price;
  if (plan?.price != null) return plan.price;
  return null;
}

function getStorageInfo(subscription, field) {
  const raw = subscription?.[field];
  if (!raw || typeof raw !== 'object') {
    return {
      percent: 0,
      totalLabel: '—',
      availableLabel: '—',
    };
  }
  const percent = Math.max(0, Math.min(100, Number(raw.usage_percentage) || 0));
  return {
    percent,
    totalLabel: raw.limit_formatted || (raw.is_unlimited ? 'Unlimited' : '—'),
    availableLabel: raw.available_formatted || (raw.is_unlimited ? 'Unlimited' : '—'),
  };
}

function expiryTone(days) {
  if (days == null) return 'safe';
  if (days < 0) return 'urgent';
  if (days <= 7) return 'urgent';
  if (days <= 30) return 'warn';
  return 'safe';
}

function expiryLabel(days) {
  if (days == null) return 'Safe';
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  if (days <= 30) return `${days} days`;
  return 'Safe';
}

const BusinessDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [taskStats, setTaskStats] = useState({ total: 0, overdue: 0, inProgress: 0 });
  const [projectHealth, setProjectHealth] = useState({
    progress: 0,
    delayed: 0,
    stable: true,
    members: [],
  });
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [credits, setCredits] = useState({ purchased: 0, balance: 0, usage: 0 });
  const [subscriptions, setSubscriptions] = useState([]);
  const [vaultAccess, setVaultAccess] = useState([]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const corporateId = userData?.corporateId || userData?.id;
      const { from: rangeFrom, to: rangeTo } = getRangeBounds(DASHBOARD_RANGE_DAYS);

      const [tasksRes, inProgressRes, projectsRes, docsRes, creditRes, subsRes, vaultRes] = await Promise.allSettled([
        axiosInstance.get('/task/list-by-sections', {
          params: {
            CorporateId: corporateId,
            // Small page size — we use pagination.total for counts (same source as Task List)
            limit: 1,
            page: 1,
          },
        }),
        axiosInstance.get('/task/list', {
          params: {
            status: 1,
            limit: 1000,
            page: 1,
          },
        }),
        axiosInstance.get('/project/list', {
          params: {
            limit: 200,
            page: 1,
          },
        }),
        axiosInstance.get('/documents/list', {
          params: {
            corporate_id: corporateId,
            limit: 100,
            page: 1,
          },
        }),
        getCreditOverview(),
        getActiveSubscriptions(true),
        getVaultCryptoActivities({ page: 1, limit: 20 }),
      ]);

      if (tasksRes.status === 'fulfilled') {
        const sections = tasksRes.value?.data?.sections || {};
        const sectionTotal = (key) => {
          const block = sections[key] || {};
          const fromPagination = Number(block.pagination?.total);
          if (Number.isFinite(fromPagination) && fromPagination >= 0) return fromPagination;
          return Array.isArray(block.tasks) ? block.tasks.length : 0;
        };
        const overdue = sectionTotal('overdue');
        const today = sectionTotal('today');
        const future = sectionTotal('future');
        let inProgress = 0;
        if (inProgressRes.status === 'fulfilled') {
          const rows =
            inProgressRes.value?.data?.tasks ||
            inProgressRes.value?.data?.data ||
            inProgressRes.value?.data ||
            [];
          inProgress = Array.isArray(rows) ? rows.length : 0;
        }
        setTaskStats({
          total: today + future + overdue,
          overdue,
          inProgress,
        });
      }

      if (projectsRes.status === 'fulfilled') {
        const projects =
          projectsRes.value?.data?.projects ||
          projectsRes.value?.data?.data ||
          projectsRes.value?.data ||
          [];
        const raw = Array.isArray(projects) ? projects : [];
        const list = raw.filter((p) => projectInSelectedRange(p, rangeFrom, rangeTo));
        const today = startOfDay(new Date());
        const delayed = list.filter((p) => {
          if (!p?.end_date) return false;
          const status = Number(p.status);
          if (status === 3) return false;
          return startOfDay(p.end_date) < today;
        }).length;

        let completedWeight = 0;
        list.forEach((p) => {
          const status = Number(p.status);
          if (status === 3) completedWeight += 100;
          else if (status === 1) completedWeight += 55;
          else if (status === 2) completedWeight += 35;
          else completedWeight += 15;
        });
        const progress = list.length ? Math.round(completedWeight / list.length) : 0;

        const memberMap = new Map();
        list.forEach((p) => {
          (p.ProjectMembers || p.members || []).forEach((m) => {
            const user = m.CorporateUser || m;
            const id = user?.id || m.user_id;
            const name = user?.full_name || user?.name || m.full_name;
            if (id && name && !memberMap.has(id)) memberMap.set(id, name);
          });
        });

        setProjectHealth({
          progress,
          delayed,
          stable: delayed === 0,
          members: Array.from(memberMap.values()).slice(0, 5),
        });
      }

      if (docsRes.status === 'fulfilled') {
        const payload = docsRes.value?.data?.data || docsRes.value?.data || {};
        const docs = payload.documents || payload.data || docsRes.value?.data?.documents || [];
        const list = Array.isArray(docs) ? docs : [];
        // Expiring soon relative to selected range: expire within next N days, or expired in last N days.
        const withExpiry = list
          .map((doc) => {
            const days = daysUntil(doc.expiry_date);
            return {
              id: doc.id,
              title: doc.title || doc.document_name || doc.name || 'Untitled document',
              expiry_date: doc.expiry_date,
              days,
            };
          })
          .filter((d) => d.days != null && d.days <= DASHBOARD_RANGE_DAYS && d.days >= -DASHBOARD_RANGE_DAYS)
          .sort((a, b) => a.days - b.days)
          .slice(0, 3);
        setExpiringDocs(withExpiry);
      }

      if (creditRes.status === 'fulfilled') {
        const data = creditRes.value;
        const balance = Number(data?.credits?.total_balance) || 0;
        const batches = Array.isArray(data?.credits?.batches) ? data.credits.batches : [];
        // Prefer purchases/usage that fall in the selected range when batch dates exist.
        const batchesInRange = batches.filter((b) =>
          itemTouchesRange([b.purchased_at, b.created_at, b.start_date, b.end_date], rangeFrom, rangeTo)
        );
        const scopedBatches = batchesInRange.length ? batchesInRange : batches;
        const purchased = scopedBatches.reduce((sum, b) => sum + (Number(b.purchased) || 0), 0);
        const used = scopedBatches.reduce((sum, b) => sum + (Number(b.used) || 0), 0);
        const usage = purchased > 0 ? Math.round((used / purchased) * 100) : 0;
        const purchasedFallback =
          purchased ||
          (Array.isArray(data?.recent_purchases)
            ? data.recent_purchases
                .filter((p) =>
                  itemTouchesRange([p.created_at, p.purchased_at, p.date], rangeFrom, rangeTo)
                )
                .reduce((s, p) => s + (Number(p.credits) || Number(p.amount) || 0), 0)
            : 0);
        setCredits({
          purchased: purchasedFallback || purchased,
          balance,
          usage: Math.max(0, Math.min(100, usage)),
        });
      }

      if (subsRes.status === 'fulfilled') {
        setSubscriptions(subsRes.value?.subscriptions || []);
      }

      if (vaultRes.status === 'fulfilled') {
        const rows =
          vaultRes.value?.data?.data ||
          vaultRes.value?.data?.activities ||
          vaultRes.value?.data ||
          [];
        const list = Array.isArray(rows) ? rows : [];
        const inRange = list.filter((row) =>
          isDateInRange(row.created_at || row.timestamp, rangeFrom, rangeTo)
        );
        setVaultAccess(
          inRange.slice(0, 2).map((row) => ({
            id: row.id,
            name: row.actor_name || row.user_name || row.CorporateUser?.full_name || 'Vault user',
            detail:
              row.description ||
              row.action_label ||
              row.action ||
              row.event_type ||
              'Vault activity',
            at: row.created_at || row.timestamp,
          }))
        );
      }
    } catch (err) {
      console.error('Dashboard load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const subscriptionCards = SUBSCRIPTION_MODULES.map((key) => {
    const sub = pickSubscription(subscriptions, key);
    return {
      key,
      label: key === 'invoice_scanner' ? 'Snap Bills' : getModuleTabLabel(key),
      icon: getModuleTabIcon(key),
      plan: sub ? getPlanName(sub) : '—',
      start: sub?.start_date ? formatDateToDDMMMYYYY(sub.start_date) : '—',
      end: sub?.end_date ? formatDateToDDMMMYYYY(sub.end_date) : '—',
      cost: sub ? formatMoney(getSubscriptionCost(sub)) : '—',
      hasPlan: !!sub,
    };
  });

  const storageCards = STORAGE_MODULES.map((mod) => {
    const sub = pickSubscription(subscriptions, mod.key);
    const info = getStorageInfo(sub, mod.storageField);
    return { ...mod, ...info, hasData: !!sub };
  });

  if (loading) {
    return <LoadingSpinner label="Loading overview…" />;
  }

  return (
    <div className="ops-dashboard">
      <div className="ops-dashboard__top">
        <div>
          <h1 className="ops-dashboard__title">Operations Overview</h1>
          <p className="ops-dashboard__subtitle">
            Performance metrics across your workspace.
          </p>
        </div>
      </div>

      <div className="ops-dashboard__metrics">
        <article className="ops-card ops-card--metric">
          <h2 className="ops-card__title">Pending Tasks</h2>
          <Link to="/task/tasklist?my=0" className="ops-pending__total" title="View all tasks">
            {taskStats.total}
          </Link>
          <div className="ops-pending__label">TOTAL</div>
          <div className="ops-pending__split">
            <Link
              to="/task/tasklist?section=overdue&my=0"
              className="ops-pending__box is-overdue"
              title="View overdue tasks"
            >
              <span>OVERDUE</span>
              <strong>{taskStats.overdue}</strong>
            </Link>
            <Link
              to="/task/tasklist?status=In%20Progress&my=0"
              className="ops-pending__box"
              title="View in-progress tasks"
            >
              <span>IN PROGRESS</span>
              <strong>{taskStats.inProgress}</strong>
            </Link>
          </div>
        </article>

        <article className="ops-card ops-card--metric">
          <div className="ops-health__head">
            <h2 className="ops-card__title">Project Health</h2>
            <span className={`ops-health__badge${projectHealth.stable ? ' is-stable' : ' is-risk'}`}>
              {projectHealth.stable ? 'STABLE' : 'AT RISK'}
            </span>
          </div>
          <div className="ops-health__progress-label">
            <span>Overall Progress</span>
            <strong>{projectHealth.progress}%</strong>
          </div>
          <div className="ops-progress">
            <div className="ops-progress__bar is-red" style={{ width: `${projectHealth.progress}%` }} />
          </div>
          <Link
            to="/task/projectlist?delayed=1"
            className="ops-health__alert"
            title="View delayed projects"
          >
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <span>
              {projectHealth.delayed} Delayed Project{projectHealth.delayed === 1 ? '' : 's'}
            </span>
          </Link>
          <div className="ops-health__teams">
            <span className="ops-health__teams-label">ACTIVE TEAMS</span>
            <div className="ops-health__avatars">
              {projectHealth.members.length ? (
                projectHealth.members.map((name) => (
                  <span key={name} className="ops-avatar" title={name}>
                    {initials(name)}
                  </span>
                ))
              ) : (
                <span className="ops-muted">No active members</span>
              )}
            </div>
          </div>
        </article>

        <article className="ops-card ops-card--metric">
          <h2 className="ops-card__title">Expiring Soon</h2>
          <ul className="ops-expire__list">
            {expiringDocs.length ? (
              expiringDocs.map((doc) => (
                <li key={doc.id}>
                  <Link
                    to={`/documents/details/${doc.id}`}
                    className="ops-expire__item"
                    title={`Open ${doc.title}`}
                  >
                    <span className="ops-expire__icon" aria-hidden="true">
                      <i className="fas fa-file-alt" />
                    </span>
                    <div className="ops-expire__meta">
                      <strong>{doc.title}</strong>
                      <span>
                        {doc.expiry_date ? formatDateToDDMMMYYYY(doc.expiry_date) : '—'}
                      </span>
                    </div>
                    <span className={`ops-expire__days is-${expiryTone(doc.days)}`}>
                      {expiryLabel(doc.days)}
                    </span>
                  </Link>
                </li>
              ))
            ) : (
              <li className="ops-empty">No documents expiring soon</li>
            )}
          </ul>
          <Link to="/documents/mydocuments" className="ops-card__link">
            View All Documents
          </Link>
        </article>

        <article className="ops-card ops-card--metric">
          <h2 className="ops-card__title">Credit Details</h2>
          <div className="ops-credit__stack">
            <div className="ops-credit__box is-purchased">
              <span>TOTAL CREDIT PURCHASED</span>
              <strong>{formatMoney(credits.purchased)}</strong>
            </div>
            <div className="ops-credit__box is-balance">
              <span>CURRENT BALANCE</span>
              <strong>{formatMoney(credits.balance)}</strong>
            </div>
          </div>
          <div className="ops-credit__usage">
            <div className="ops-health__progress-label">
              <span>Usage Level</span>
              <strong>{credits.usage}%</strong>
            </div>
            <div className="ops-progress">
              <div className="ops-progress__bar is-amber" style={{ width: `${credits.usage}%` }} />
            </div>
          </div>
        </article>
      </div>

      <section className="ops-section">
        <h3 className="ops-section__title">Subscription Details</h3>
        <div className="ops-dashboard__subs">
          {subscriptionCards.map((card) => (
            <article key={card.key} className="ops-card ops-card--sub">
              <header className="ops-card__grad-head">
                <span className="ops-card__grad-icon" aria-hidden="true">
                  <i className={card.icon} />
                </span>
                <h4>{card.label}</h4>
              </header>
              <dl className="ops-sub__rows">
                <div>
                  <dt>Plan</dt>
                  <dd className={card.hasPlan ? 'is-plan' : ''}>{card.plan}</dd>
                </div>
                <div>
                  <dt>Start Date</dt>
                  <dd>{card.start}</dd>
                </div>
                <div>
                  <dt>End Date</dt>
                  <dd>{card.end}</dd>
                </div>
                <div>
                  <dt>Cost</dt>
                  <dd>{card.cost}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section className="ops-section">
        <h3 className="ops-section__title">Storage Usage</h3>
        <div className="ops-dashboard__storage">
          {storageCards.map((card) => (
            <article key={card.key} className="ops-card ops-card--storage">
              <div className="ops-storage__head">
                <h4>{card.label}</h4>
                <span>{card.percent}% Used</span>
              </div>
              <div className="ops-progress ops-progress--lg">
                <div
                  className={`ops-progress__bar ${card.barClass}`}
                  style={{ width: `${card.percent}%` }}
                />
              </div>
              <div className="ops-storage__footer">
                <div>
                  <span>TOTAL ALLOWED</span>
                  <strong>{card.totalLabel}</strong>
                </div>
                <div>
                  <span>AVAILABLE</span>
                  <strong className="is-available">{card.availableLabel}</strong>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="ops-section">
        <h3 className="ops-section__title">Latest Vault Access</h3>
        <div className="ops-dashboard__vault">
          {vaultAccess.length ? (
            vaultAccess.map((item) => (
              <article key={item.id || item.at} className="ops-card ops-card--vault">
                <span className="ops-vault__icon" aria-hidden="true">
                  <i className="fas fa-user-shield" />
                </span>
                <div className="ops-vault__body">
                  <strong>{item.name}</strong>
                  <p>{item.detail}</p>
                </div>
                <time>{formatRelativeTime(item.at)}</time>
              </article>
            ))
          ) : (
            <>
              <article className="ops-card ops-card--vault is-empty">
                <span className="ops-vault__icon" aria-hidden="true">
                  <i className="fas fa-user-shield" />
                </span>
                <div className="ops-vault__body">
                  <strong>No recent vault activity</strong>
                  <p>Vault access events will appear here.</p>
                </div>
              </article>
              <article className="ops-card ops-card--vault is-empty">
                <span className="ops-vault__icon" aria-hidden="true">
                  <i className="fas fa-lock" />
                </span>
                <div className="ops-vault__body">
                  <strong>Credentials Vault</strong>
                  <p>
                    <Link to="/credential-vault/vaultSettings">Open vault settings</Link>
                  </p>
                </div>
              </article>
            </>
          )}
        </div>
      </section>
      <InternalFooter />
    </div>
  );
};

export default BusinessDashboard;
